import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Heart, Bookmark, Star, Store, MapPin, Phone, Mail, Globe,
  ShoppingBag, Send, Truck, X, ExternalLink, CheckCircle2, Flag, MoreVertical,
  Navigation as NavIcon, ChevronLeft, ChevronRight, PhoneCall, MessageSquare,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import Navigation from '../components/Navigation';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { PUBLIC_APP_URL, shareContent } from '../utils/shareContent';
import MyFoodDocuments, { MyFoodDocsBadge } from '../components/MyFoodDocuments';

const CGV_URL = 'https://info.destine.studio/document';

const ShopDetail = () => {
  const navigate = useNavigate();
  const { id: shopId } = useParams();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);   // product object being purchased
  const [reviewing, setReviewing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [fullscreenProduct, setFullscreenProduct] = useState(null);  // {photos, title} for fullscreen viewer

  useEffect(() => { load(); /* eslint-disable-line */ }, [shopId]);

  useEffect(() => {
    if (!shopId) return;
    const started = Date.now();
    axios.post('/api/ads-commercial/track', { kind: 'shop_view', shop_id: shopId }).catch(() => {});
    return () => {
      const seconds = Math.round((Date.now() - started) / 1000);
      if (seconds >= 2) axios.post('/api/ads-commercial/track', { kind: 'shop_time', shop_id: shopId, seconds }).catch(() => {});
    };
  }, [shopId]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, r] = await Promise.all([
        axios.get(`/api/shop/shops/${shopId}`),
        axios.get(`/api/shop/shops/${shopId}/products`),
        axios.get(`/api/shop/shops/${shopId}/reviews`),
      ]);
      setShop(s.data); setProducts(p.data?.products || []); setReviews(r.data?.reviews || []);
    } catch {
      toast.error('Boutique introuvable');
    } finally { setLoading(false); }
  };

  const toggleSave = async () => {
    if (!user) return toast.error(t('common.login_required', 'Connexion requise'));
    const r = await axios.post(`/api/shop/shops/${shopId}/save`);
    setShop(s => ({ ...s, viewer_saved: r.data.saved }));
  };

  const goBack = () => {
    // Go back to Studio tab in Discover (Films)
    navigate('/films?tab=studio');
  };

  if (loading || !shop) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        {t('common.loading', 'Chargement…')}
      </div>
    );
  }

  const fmtPrice = (cents, currency) => {
    const v = (cents || 0) / 100;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || shop.currency || 'eur').toUpperCase() }).format(v); }
    catch { return `${v.toFixed(2)} ${currency || 'EUR'}`; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-950 via-black to-violet-950 text-white pb-24" data-testid="shop-detail">
      {/* Cover */}
      <div className="relative h-[220px] sm:h-[280px] bg-gradient-to-br from-fuchsia-700/30 to-violet-800/30">
        {shop.cover_url
          ? <img src={getMediaUrl(shop.cover_url)} alt={shop.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Store className="w-16 h-16 text-white/30" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <button onClick={goBack} data-keep-dark="true" className="absolute top-4 left-4 p-2 rounded-full bg-black/60 backdrop-blur text-white" data-testid="shop-detail-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="absolute top-4 right-4 flex gap-2 items-start">
          <button onClick={toggleSave} data-keep-dark="true" className="p-2 rounded-full bg-black/60 backdrop-blur text-white" data-testid="shop-detail-save">
            <Bookmark className={`w-5 h-5 ${shop.viewer_saved ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`} />
          </button>
          {/* 3-dot menu — Signaler (other actions later) */}
          <div className="relative">
            <button onClick={() => setShowMenu(s => !s)} data-keep-dark="true" className="p-2 rounded-full bg-black/60 backdrop-blur text-white" data-testid="shop-detail-menu">
              <MoreVertical className="w-5 h-5" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 mt-1 w-48 rounded-2xl bg-gray-900 border border-white/10 shadow-2xl overflow-hidden z-50" data-testid="shop-detail-menu-dropdown">
                  {user && user.id !== shop.owner_id && (
                    <button
                      onClick={() => { setShowMenu(false); setReporting(true); }}
                      className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-2 text-red-300"
                      data-testid="shop-detail-menu-report"
                    >
                      <Flag className="w-4 h-4" /> {t('eshop.report_shop', 'Signaler la boutique')}
                    </button>
                  )}
                  <button
                    onClick={() => { setShowMenu(false); navigate('/shop/my-orders'); }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-2"
                    data-testid="shop-detail-menu-orders"
                  >
                    <ShoppingBag className="w-4 h-4 text-fuchsia-300" /> {t('eshop.my_orders', 'Mes commandes')}
                  </button>
                  <button
                    onClick={async () => {
                      setShowMenu(false);
                      const shareUrl = `${PUBLIC_APP_URL}/shop/${shopId}`;
                      try {
                        await shareContent({
                          title: shop.name,
                          text: shop.description || shop.name,
                          url: shareUrl,
                        });
                      } catch {
                        try { await navigator.clipboard?.writeText(shareUrl); } catch { /* ignore */ }
                        toast.success(t('common.link_copied', 'Lien copié'));
                      }
                    }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-2"
                    data-testid="shop-detail-menu-share"
                  >
                    <Send className="w-4 h-4" /> {t('common.copy_link', 'Copier le lien')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="text-2xl font-bold drop-shadow">{shop.name}</h1>
          <div className="flex items-center gap-2 text-xs text-gray-200 mt-1">
            <span data-keep-dark="true" className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white">{t(`eshop.cat_${shop.category}`) || shop.category}</span>
            {shop.country && <span data-keep-dark="true" className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white">{shop.country}</span>}
            {shop.rating_count > 0 && (
              <span className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                {shop.rating_avg?.toFixed?.(1) || shop.rating_avg} <span className="opacity-70">({shop.rating_count})</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-5">
        {/* Description */}
        {shop.description && (
          <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{shop.description}</p>
        )}

        {/* Contact strip — v50.2: single column on mobile so long emails / addresses
            wrap instead of being ellipsized ("…") when the screen is narrow. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {shop.address && <Info icon={<MapPin className="w-3.5 h-3.5" />} label={shop.address} />}
          {shop.phone && <Info icon={<Phone className="w-3.5 h-3.5" />} label={shop.phone} />}
          {shop.contact_email && <Info icon={<Mail className="w-3.5 h-3.5" />} label={shop.contact_email} />}
          {shop.website_url && (
            <a href={shop.website_url} target="_blank" rel="noreferrer" className="flex items-start gap-2 px-3 py-2 bg-white/5 rounded-xl hover:bg-white/10 break-all">
              <Globe className="w-3.5 h-3.5 text-fuchsia-300 flex-shrink-0 mt-0.5" />
              <span className="text-fuchsia-200 break-all flex-1">{shop.website_url.replace(/^https?:\/\//, '')}</span>
              <ExternalLink className="w-3 h-3 ml-auto flex-shrink-0 mt-0.5" />
            </a>
          )}
        </div>

        {/* OSM map + open-in-maps buttons */}
        {(shop.lat && shop.lng) ? (
          <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="shop-osm-map">
            <iframe
              title="shop-map"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${shop.lng - 0.005},${shop.lat - 0.003},${shop.lng + 0.005},${shop.lat + 0.003}&layer=mapnik&marker=${shop.lat},${shop.lng}`}
              className="w-full h-44 border-0"
              loading="lazy"
            />
            <div className="flex gap-1 p-2 bg-white/5 border-t border-white/10">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${shop.lat},${shop.lng}`}
                target="_blank" rel="noreferrer"
                className="flex-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-center flex items-center justify-center gap-1"
                data-testid="shop-open-google-maps"
              >
                <NavIcon className="w-3 h-3" /> Google Maps
              </a>
              <a
                href={`https://maps.apple.com/?ll=${shop.lat},${shop.lng}&q=${encodeURIComponent(shop.name)}`}
                target="_blank" rel="noreferrer"
                className="flex-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-center flex items-center justify-center gap-1"
                data-testid="shop-open-apple-maps"
              >
                <NavIcon className="w-3 h-3" /> Plans (Apple)
              </a>
            </div>
          </div>
        ) : shop.address ? (
          /* No lat/lng on file → geocode the address client-side via Nominatim
             then render the embed; meanwhile show "Open in maps" links that
             work with text-only addresses. */
          <ShopMapFromAddress address={shop.address} name={shop.name} />
        ) : null}

        {/* v49.4 — MyFood restaurants: hygiene / permit / other documents */}
        <MyFoodDocuments shop={shop} />

        {/* Shipping */}
        {(shop.shipping_terms || shop.shipping_fee_cents > 0) && (
          <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
            <p className="text-xs font-semibold flex items-center gap-2 mb-1">
              <Truck className="w-3.5 h-3.5 text-fuchsia-300" /> {t('eshop.shipping', 'Livraison')}
            </p>
            {shop.shipping_fee_cents > 0 && (
              <p className="text-xs text-gray-400">{t('eshop.shipping_fee', 'Frais')} : <span className="text-white font-semibold">{fmtPrice(shop.shipping_fee_cents, shop.currency)}</span></p>
            )}
            {shop.shipping_terms && <p className="text-xs text-gray-300 mt-1 whitespace-pre-line">{shop.shipping_terms}</p>}
          </div>
        )}

        {/* Products */}
        <div>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-fuchsia-400" /> {t('eshop.catalog', 'Catalogue')} ({products.length})
          </h2>
          {products.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center bg-white/5 rounded-2xl">{t('eshop.no_products', 'Aucun produit pour le moment')}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {products.map(p => (
                <div key={p.id} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid={`shop-product-${p.id}`}>
                  {p.photos?.[0] ? (
                    <img
                      src={getMediaUrl(p.photos[0])}
                      alt={p.title}
                      onClick={() => setFullscreenProduct(p)}
                      className="w-full aspect-square object-cover cursor-pointer"
                      data-testid={`shop-product-image-${p.id}`}
                    />
                  ) : (
                    <div className="w-full aspect-square bg-white/5 flex items-center justify-center">
                      <ShoppingBag className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                  <div className="p-2.5 space-y-1">
                    <h4 className="font-semibold text-sm truncate">{p.title}</h4>
                    <p className="text-[11px] text-gray-400 line-clamp-2">{p.description}</p>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-fuchsia-300 font-bold text-sm">{fmtPrice(p.price_cents, p.currency)}</span>
                      <button
                        onClick={() => setBuying(p)}
                        className="px-2.5 py-1 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-[11px] font-semibold"
                        data-testid={`shop-buy-${p.id}`}
                      >
                        {p.kind === 'service'
                          ? (t('eshop.contact', 'Contacter'))
                          : (t('eshop.buy', 'Acheter'))}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span className="flex items-center" data-testid="shop-rating-stars">
                {[1,2,3,4,5].map(i => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i <= Math.round(Number(shop.rating_avg || 0))
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-600'
                    }`}
                  />
                ))}
              </span>
              <span className="text-sm font-semibold text-yellow-400 ml-1">
                {Number(shop.rating_avg || 0).toFixed(1)}
              </span>
              <span className="text-sm text-gray-300 font-normal">
                {t('eshop.reviews', 'Avis')}
                <span className="text-gray-500"> ({reviews.length})</span>
              </span>
            </h2>
          </div>
          {/* Big CTA card to invite the user to leave a review */}
          {user && user.id !== shop.owner_id && (
            <button
              onClick={() => setReviewing(true)}
              className="w-full mb-3 p-4 rounded-2xl bg-gradient-to-r from-yellow-500/15 to-fuchsia-500/15 border border-yellow-400/30 hover:border-yellow-400/60 flex items-center justify-between"
              data-testid="shop-review-cta"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">{t('eshop.write_review', 'Donner un avis')}</p>
                  <p className="text-xs text-gray-400">{t('eshop.rate_help', '1 à 5 étoiles + commentaire')}</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 rotate-180 text-yellow-400" />
            </button>
          )}
          <div className="space-y-2">
            {reviews.map(r => (
              <div key={r.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`shop-review-${r.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  {r.user?.profile_image_url
                    ? <img src={getMediaUrl(r.user.profile_image_url)} alt="" className="w-7 h-7 rounded-full object-cover" />
                    : <div className="w-7 h-7 rounded-full bg-gradient-to-br from-fuchsia-600 to-violet-700 flex items-center justify-center text-xs font-bold">{(r.user?.name || '?').charAt(0)}</div>}
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{r.user?.name || '—'}</p>
                    <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`w-3 h-3 ${i <= r.stars ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`} />)}</div>
                  </div>
                </div>
                {r.comment && <p className="text-sm text-gray-300 whitespace-pre-line">{r.comment}</p>}
              </div>
            ))}
            {reviews.length === 0 && <p className="text-sm text-gray-500 text-center py-4">{t('eshop.no_reviews', 'Aucun avis pour le moment')}</p>}
          </div>
        </div>

        {/* CGV link */}
        <a href={CGV_URL} target="_blank" rel="noreferrer" className="block text-xs text-fuchsia-300 hover:text-fuchsia-200 text-center pt-2" data-testid="shop-cgv-link">
          {t('eshop.cgv_link', 'Conditions générales de vente')} <ExternalLink className="w-3 h-3 inline ml-1" />
        </a>
      </div>

      {buying && (
        buying.kind === 'service' ? (
          <ServiceContactModal
            shop={shop}
            product={buying}
            onClose={() => setBuying(null)}
            onSent={() => { setBuying(null); toast.success(t('eshop.callback_sent', 'Votre demande a été envoyée')); }}
          />
        ) : (
          <CheckoutModal
            shop={shop}
            product={buying}
            onClose={() => setBuying(null)}
            onPaid={() => { setBuying(null); toast.success(t('eshop.order_placed', 'Commande passée')); }}
          />
        )
      )}
      {reviewing && (
        <ReviewModal
          shopId={shopId}
          onClose={() => setReviewing(false)}
          onCreated={() => { setReviewing(false); load(); }}
        />
      )}
      {reporting && (
        <ReportShopModal
          shopId={shopId}
          onClose={() => setReporting(false)}
          onSent={() => { setReporting(false); toast.success(t('eshop.report_sent', 'Signalement envoyé. Merci.')); }}
        />
      )}
      {fullscreenProduct && (
        <ProductFullscreenViewer
          product={fullscreenProduct}
          onClose={() => setFullscreenProduct(null)}
          onBuy={() => { const p = fullscreenProduct; setFullscreenProduct(null); setBuying(p); }}
          fmtPrice={fmtPrice}
          buyLabel={
            fullscreenProduct.kind === 'service'
              ? (t('eshop.contact', 'Contacter'))
              : (t('eshop.buy', 'Acheter'))
          }
        />
      )}

      <Navigation />
    </div>
  );
};

const Info = ({ icon, label }) => (
  <div className="flex items-start gap-2 px-3 py-2 bg-white/5 rounded-xl">
    <span className="text-fuchsia-300 flex-shrink-0 mt-0.5">{icon}</span>
    <span className="text-gray-200 break-words flex-1 min-w-0">{label}</span>
  </div>
);

// =========================== Address-only OSM map ===========================
// When a shop only has a postal address (no lat/lng), we geocode it client-side
// via the public Nominatim service and render the same OSM embed. We also
// surface "Open in Google / Apple Maps" links that work with text addresses so
// the fallback is useful even before geocoding completes.
const ShopMapFromAddress = ({ address, name }) => {
  const [coords, setCoords] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!address) return;
    (async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data[0]?.lat && data[0]?.lon) {
          setCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [address]);

  const gmaps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  const amaps = `https://maps.apple.com/?q=${encodeURIComponent(address)}`;

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="shop-osm-map">
      {coords ? (
        <iframe
          title="shop-map"
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${coords.lng - 0.005},${coords.lat - 0.003},${coords.lng + 0.005},${coords.lat + 0.003}&layer=mapnik&marker=${coords.lat},${coords.lng}`}
          className="w-full h-44 border-0"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-44 flex items-center justify-center bg-white/5 text-xs text-gray-400 gap-2">
          <MapPin className="w-4 h-4 text-fuchsia-300" />
          {failed ? (address || '—') : 'Localisation…'}
        </div>
      )}
      <div className="flex gap-1 p-2 bg-white/5 border-t border-white/10">
        <a href={gmaps} target="_blank" rel="noreferrer"
          className="flex-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-center flex items-center justify-center gap-1"
          data-testid="shop-open-google-maps">
          <NavIcon className="w-3 h-3" /> Google Maps
        </a>
        <a href={amaps} target="_blank" rel="noreferrer"
          className="flex-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-center flex items-center justify-center gap-1"
          data-testid="shop-open-apple-maps">
          <NavIcon className="w-3 h-3" /> Plans (Apple)
        </a>
      </div>
    </div>
  );
};

// =========================== Service contact modal ===========================
// For products of kind 'service', Stripe is bypassed. The buyer can either
// call the seller directly or request a callback (the seller receives a
// notification with the buyer's phone number — surfaced in ProSpace).
const ServiceContactModal = ({ shop, product, onClose, onSent, onPaid }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const kbHeight = useKeyboardHeight();
  const [mode, setMode] = React.useState('choice'); // choice | callback | buy
  const [name, setName] = React.useState(user?.name || '');
  const [phone, setPhone] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 6)
      return toast.error(t('eshop.invalid_phone', 'Numéro de téléphone invalide'));
    setBusy(true);
    try {
      await axios.post(`/api/shop/shops/${shop.id}/service-callback`, {
        product_id: product.id,
        buyer_name: name || (user?.name || ''),
        buyer_phone: phone,
        message,
      });
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  // Direct purchase short-circuit — render the CheckoutModal as the active modal.
  if (mode === 'buy') {
    return (
      <CheckoutModal
        shop={shop}
        product={product}
        onClose={onClose}
        onPaid={onPaid || onClose}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="service-contact-modal"
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}
    >
      <div
        className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '90vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="font-semibold text-white">
            {product.title}
          </h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="service-contact-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {mode === 'choice' ? (
          <div className="p-4 space-y-3">
            <p className="text-xs text-gray-400">
              {t('eshop.service_intro', "Ce service ne se paie pas en ligne. Choisissez comment contacter le prestataire pour discuter du projet et obtenir un devis.")}
            </p>
            <a
              href={shop.phone ? `tel:${shop.phone.replace(/\s/g, '')}` : undefined}
              onClick={(e) => { if (!shop.phone) { e.preventDefault(); toast.error(t('eshop.no_phone_provided', 'Numéro non renseigné')); } }}
              className="w-full p-3 rounded-2xl bg-emerald-600/15 border border-emerald-500/40 hover:bg-emerald-600/25 flex items-center gap-3"
              data-testid="service-contact-call"
            >
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <PhoneCall className="w-5 h-5 text-emerald-300" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-sm text-white">{t('eshop.call_seller', 'Appeler')}</p>
                <p className="text-xs text-emerald-200/80">{shop.phone || (t('eshop.no_phone_provided', 'Numéro non renseigné'))}</p>
              </div>
            </a>
            <button
              onClick={() => setMode('callback')}
              className="w-full p-3 rounded-2xl bg-fuchsia-600/15 border border-fuchsia-500/40 hover:bg-fuchsia-600/25 flex items-center gap-3"
              data-testid="service-contact-callback"
            >
              <div className="w-10 h-10 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-fuchsia-300" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-sm text-white">{t('eshop.request_callback', 'Demander un rappel')}</p>
                <p className="text-xs text-fuchsia-200/80">{t('eshop.request_callback_help', 'Laissez votre numéro, le vendeur vous rappellera')}</p>
              </div>
            </button>
            {/* Direct purchase: if the buyer has already agreed with the seller out-of-band
                they can pay the service straight away. Uses the same Stripe checkout as
                physical products (5% Destine commission). */}
            <button
              onClick={() => setMode('buy')}
              className="w-full p-3 rounded-2xl bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 flex items-center gap-3"
              data-testid="service-contact-buy"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-amber-300" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-sm text-white">{t('eshop.buy_service_now', 'Acheter le service')}</p>
                <p className="text-xs text-amber-200/80">{t('eshop.buy_service_help', 'Si vous êtes déjà d’accord avec le vendeur — paiement sécurisé Stripe')}</p>
              </div>
            </button>
            <p className="text-[11px] text-center text-gray-500 pt-1">
              {t('eshop.service_no_stripe', 'Pour les services, aucun paiement n’est traité par Destine.')}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <button
              onClick={() => setMode('choice')}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
              data-testid="service-callback-back"
            >
              <ArrowLeft className="w-3 h-3" /> {t('common.back', 'Retour')}
            </button>
            <Field label={t('eshop.fullname', 'Nom complet')} value={name} setValue={setName} testid="service-callback-name" />
            <Field label={t('eshop.phone', 'Téléphone')} value={phone} setValue={setPhone} type="tel" testid="service-callback-phone" />
            <Field label={t('eshop.callback_message', 'Message (facultatif)')} value={message} setValue={setMessage} textarea testid="service-callback-message" />
            <button
              onClick={submit}
              disabled={busy}
              className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2"
              data-testid="service-callback-submit"
            >
              {busy ? (t('common.loading', 'Envoi…')) : <><Send className="w-4 h-4" /> {t('eshop.send_callback_request', 'Envoyer la demande')}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CheckoutModal = ({ shop, product, onClose, onPaid }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const kbHeight = useKeyboardHeight();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [qty, setQty] = useState(1);
  const [cgv, setCgv] = useState(false);
  const [busy, setBusy] = useState(false);

  const isService = (product?.kind || '').toLowerCase() === 'service';
  const shippingFee = isService ? 0 : (shop.shipping_fee_cents || 0);
  const total = (product.price_cents * qty + shippingFee) / 100;
  const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: (product.currency || shop.currency || 'eur').toUpperCase() });

  const submit = async () => {
    if (!cgv) return toast.error(t('eshop.must_accept_cgv', 'Vous devez accepter les CGV'));
    if (!name || !email || !phone || !address) return toast.error(t('eshop.fill_all', 'Veuillez compléter tous les champs'));
    setBusy(true);
    try {
      const r = await axios.post(`/api/shop/shops/${shop.id}/checkout`, {
        product_id: product.id, quantity: qty,
        buyer_name: name, buyer_email: email, buyer_phone: phone, buyer_address: address,
        cgv_accepted: true,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        onPaid?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur paiement');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      onClick={onClose} data-testid="shop-checkout-modal"
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}
    >
      <div
        className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '90vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="font-semibold text-white">{t('eshop.checkout', 'Paiement')} — {product.title}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <Field label={t('eshop.fullname', 'Nom complet')} value={name} setValue={setName} testid="checkout-name" />
          <Field label="Email" type="email" value={email} setValue={setEmail} testid="checkout-email" />
          <Field label={t('eshop.phone', 'Téléphone')} value={phone} setValue={setPhone} testid="checkout-phone" />
          <Field label={isService ? t('eshop.contact_address', 'Adresse / coordonnées') : t('eshop.address', 'Adresse de livraison')} value={address} setValue={setAddress} textarea testid="checkout-address" />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{t('eshop.quantity', 'Quantité')}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-7 h-7 rounded-full bg-white/10">-</button>
              <span className="w-6 text-center font-semibold">{qty}</span>
              <button onClick={() => setQty(q => Math.min(100, q + 1))} className="w-7 h-7 rounded-full bg-white/10">+</button>
            </div>
          </div>
          <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 p-3 space-y-1 text-xs">
            <div className="flex justify-between"><span>{t('eshop.subtotal', 'Sous-total')}</span><span>{fmt.format((product.price_cents * qty) / 100)}</span></div>
            {!isService && (
              <div className="flex justify-between"><span>{t('eshop.shipping', 'Livraison')}</span><span>{fmt.format(shippingFee / 100)}</span></div>
            )}
            <div className="flex justify-between font-bold text-sm pt-1 border-t border-fuchsia-500/30"><span>Total</span><span>{fmt.format(total)}</span></div>
          </div>
          <label className="flex items-start gap-2 text-xs text-gray-300">
            <input type="checkbox" checked={cgv} onChange={e => setCgv(e.target.checked)} data-testid="checkout-cgv-checkbox" className="mt-0.5" />
            <span>{t('eshop.accept_cgv', "J'accepte les")} <a href={CGV_URL} target="_blank" rel="noreferrer" className="text-fuchsia-300 underline">{t('eshop.cgv', 'CGV')}</a></span>
          </label>
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2" data-testid="checkout-pay-btn">
            {busy ? (t('common.loading', 'Chargement…')) : <><CheckCircle2 className="w-4 h-4" /> {t('eshop.pay', 'Payer')} {fmt.format(total)}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, setValue, type = 'text', textarea, testid }) => (
  <div>
    <label className="text-xs text-gray-400 block mb-1">{label}</label>
    {textarea ? (
      <textarea value={value} onChange={e => setValue(e.target.value)} rows={2}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500" data-testid={testid} />
    ) : (
      <input value={value} onChange={e => setValue(e.target.value)} type={type}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500" data-testid={testid} />
    )}
  </div>
);

const ReviewModal = ({ shopId, onClose, onCreated }) => {
  const { t } = useLanguage();
  const kbHeight = useKeyboardHeight();
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`/api/shop/shops/${shopId}/reviews`, { stars, comment });
      toast.success(t('eshop.review_thanks', 'Merci pour votre avis'));
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6"
      onClick={onClose}
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}
    >
      <div className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '80vh' }}
        data-testid="shop-review-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-semibold">{t('eshop.write_review', 'Donner un avis')}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="flex justify-center gap-1">
            {[1,2,3,4,5].map(i => (
              <button key={i} onClick={() => setStars(i)} data-testid={`review-star-${i}`}>
                <Star className={`w-8 h-8 ${i <= stars ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`} />
              </button>
            ))}
          </div>
          <textarea
            value={comment} onChange={e => setComment(e.target.value)} rows={4}
            placeholder={t('eshop.review_placeholder', 'Votre commentaire (facultatif)')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500"
            data-testid="review-comment"
          />
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2" data-testid="review-submit">
            <Send className="w-4 h-4" /> {t('eshop.publish_review', 'Publier')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportShopModal = ({ shopId, onClose, onSent }) => {
  const { t } = useLanguage();
  const kbHeight = useKeyboardHeight();
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const REASONS = [
    t('eshop.report_reason_scam', 'Arnaque / fraude'),
    t('eshop.report_reason_fake_product', 'Produits contrefaits'),
    t('eshop.report_reason_inappropriate', 'Contenu inapproprié'),
    t('eshop.report_reason_illegal', 'Activité illégale'),
    t('eshop.report_reason_other', 'Autre'),
  ];

  const submit = async () => {
    if (!reason) return toast.error(t('eshop.report_pick_reason', 'Sélectionnez un motif'));
    setBusy(true);
    try {
      await axios.post(`/api/shop/shops/${shopId}/report`, { reason, message });
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6"
      onClick={onClose}
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}
    >
      <div className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '80vh' }}
        data-testid="shop-report-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-semibold flex items-center gap-2"><Flag className="w-4 h-4 text-red-400" /> {t('eshop.report_shop', 'Signaler la boutique')}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <p className="text-xs text-gray-400">{t('eshop.report_help', 'Aidez-nous à modérer Destine. Les signalements sont examinés par notre équipe.')}</p>
          <div className="space-y-1.5">
            {REASONS.map(r => (
              <label key={r} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10">
                <input type="radio" checked={reason === r} onChange={() => setReason(r)} className="accent-red-500" />
                <span className="text-sm">{r}</span>
              </label>
            ))}
          </div>
          <textarea
            value={message} onChange={e => setMessage(e.target.value)} rows={3}
            placeholder={t('eshop.report_details', 'Détails complémentaires (facultatif)')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-500"
            data-testid="report-message"
          />
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2" data-testid="report-submit">
            <Flag className="w-4 h-4" /> {t('eshop.send_report', 'Envoyer le signalement')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopDetail;

// =========================== Product fullscreen viewer ===========================
// Tap an image in the catalog → open a fullscreen overlay similar to the one in
// chats. Supports horizontal swipe across multiple photos and a Buy CTA.
const ProductFullscreenViewer = ({ product, onClose, onBuy, fmtPrice, buyLabel }) => {
  const photos = (product?.photos || []).filter(Boolean);
  const [idx, setIdx] = React.useState(0);
  // Lock body scroll while modal is open
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  // Keyboard navigation
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIdx(i => Math.min(i + 1, photos.length - 1));
      else if (e.key === 'ArrowLeft') setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose]);
  const current = photos[idx];
  const isVideo = current && /\.(mp4|webm|mov)$/i.test(current);
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      data-testid="product-fullscreen-viewer"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between p-4" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20"
          data-testid="product-fullscreen-close"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        <span className="text-sm text-white/70">{idx + 1} / {photos.length || 1}</span>
        <div className="w-10" />
      </div>
      {/* Media */}
      <div className="flex-1 flex items-center justify-center px-4 relative" onClick={(e) => e.stopPropagation()}>
        {current ? (
          isVideo
            ? <video src={getMediaUrl(current)} controls autoPlay playsInline className="max-w-full max-h-full" data-testid="product-fullscreen-video" />
            : <img src={getMediaUrl(current)} alt={product.title} className="max-w-full max-h-full object-contain" data-testid="product-fullscreen-image" />
        ) : (
          <div className="text-gray-400 flex flex-col items-center gap-2">
            <ShoppingBag className="w-16 h-16" />
            <p>Aucun visuel</p>
          </div>
        )}
        {photos.length > 1 && (
          <>
            <button
              onClick={() => setIdx(i => Math.max(i - 1, 0))}
              disabled={idx === 0}
              className="absolute left-2 p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30"
              data-testid="product-fullscreen-prev"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={() => setIdx(i => Math.min(i + 1, photos.length - 1))}
              disabled={idx === photos.length - 1}
              className="absolute right-2 p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30"
              data-testid="product-fullscreen-next"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}
      </div>
      {/* Bottom info */}
      <div className="p-4 bg-black/60 backdrop-blur" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-1">{product.title}</h3>
        {product.description && <p className="text-sm text-white/70 mb-3 line-clamp-3">{product.description}</p>}
        <div className="flex items-center justify-between">
          <span className="text-fuchsia-300 font-bold text-xl">{fmtPrice ? fmtPrice(product.price_cents, product.currency) : `${(product.price_cents/100).toFixed(2)} €`}</span>
          <button
            onClick={onBuy}
            className="px-5 py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold"
            data-testid="product-fullscreen-buy"
          >
            {buyLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
